import { useT } from "../../../shared/i18n/i18n";
import { CalendarDays, CalendarRange, ChevronRight, Loader2 } from "lucide-react";
import {
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  addTimelineDays,
  buildTimelineColumns,
  buildTimelineViewportRange,
  formatTimelineDateShort,
  formatTimelineDateSpan,
  moveTimelineSpan,
  packTimelineLanes,
  resizeTimelineSpan,
  shiftTimelineRange,
  timelineBarPosition,
  timelineCellWidths,
  timelineDateAtPosition,
  timelinePosition,
  timelineScaleLabels,
  timelineSpanEquals,
  toTimelineDateInput,
  todayTimelineDate,
  type TimelineColumn,
  type TimelineDateSpan,
  type TimelineRange,
  type TimelineScale,
} from "../model/timeline";

export type TimelineRowMetaPart = {
  /** Optional swatch shown as a small dot before the label (status, select option). */
  color?: string | null;
  label: string;
};

export type TimelineRow<T> = {
  canResize?: boolean;
  color: string | null;
  end: Date | null;
  id: string;
  item: T;
  label: string;
  /** Compact extra info shown after the label while it fits on the bar. */
  meta?: TimelineRowMetaPart[] | null;
  start: Date | null;
};

export type TimelineGroup<T> = {
  color: string | null;
  items: TimelineRow<T>[];
  key: string;
  label: string;
};

export type TimelineVisibleRange = {
  endDate: string;
  startDate: string;
};

type DatedTimelineRow<T> = TimelineRow<T> & { end: Date; start: Date };

type TimelineSection<T> = {
  group: TimelineGroup<T>;
  lanes: Array<Array<{ end: Date; item: DatedTimelineRow<T>; start: Date }>>;
};

type TimelineGeometry = {
  bands: TimelineSectionBand[];
  canvasRef: RefObject<HTMLDivElement | null>;
  cellWidth: number;
  columnCount: number;
  lanesRef: RefObject<HTMLDivElement | null>;
  range: TimelineRange;
  scale: TimelineScale;
  scrollerRef: RefObject<HTMLDivElement | null>;
};

/** Vertical extent of one section (group) inside the lanes container. */
type TimelineSectionBand = {
  bottom: number;
  collapsed: boolean;
  key: string;
  laneAreaHeight: number;
  laneAreaTop: number;
  top: number;
};

const LANE_HEIGHT = 36;
const GROUP_HEADER_HEIGHT = 32;
const EDGE_SENTINEL_WIDTH = 480;

export function Timeline<T>({
  canCreateRange = true,
  collapsedGroupKeys,
  emptyMessage = "No items match this view.",
  grouped = false,
  groups,
  onChangeItemGroup,
  onCreateItem,
  onItemClick,
  onChangeItemDates,
  onToggleGroupCollapse,
  onVisibleRangeChange,
  onScaleChange,
  loading = false,
  scale,
  toolbarEnd,
}: {
  /** When false (single-date field), drawing produces a one-day span. */
  canCreateRange?: boolean;
  collapsedGroupKeys?: string[];
  emptyMessage?: string;
  /** Grouping is active: always show section headers and keep empty sections visible. */
  grouped?: boolean;
  groups: TimelineGroup<T>[];
  loading?: boolean;
  onChangeItemGroup?: (item: T, groupKey: string) => Promise<void>;
  onCreateItem?: (input: { groupKey: string | null; span: TimelineDateSpan; title: string }) => Promise<void>;
  onItemClick: (item: T) => void;
  onChangeItemDates?: (item: T, span: TimelineDateSpan) => Promise<void>;
  onToggleGroupCollapse?: (groupKey: string) => void;
  onVisibleRangeChange?: (range: TimelineVisibleRange) => void;
  onScaleChange: (scale: TimelineScale) => void;
  scale: TimelineScale;
  toolbarEnd?: ReactNode;
}) {
  const t = useT();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const lanesRef = useRef<HTMLDivElement>(null);
  const [range, setRange] = useState<TimelineRange>(() => buildTimelineViewportRange(scale));
  const cellWidth = timelineCellWidths[scale];
  const columns = useMemo(() => buildTimelineColumns(range, scale), [range, scale]);
  const timelineWidth = columns.length * cellWidth;
  const columnTemplate = `repeat(${columns.length}, ${cellWidth}px)`;

  // Reassigned after the section bands are computed below; consumers are all
  // event handlers and effects, so they never read a stale value.
  const geometryRef = useRef<TimelineGeometry>({ bands: [], canvasRef, cellWidth, columnCount: columns.length, lanesRef, range, scale, scrollerRef });

  // The span of the bar currently being dragged/resized, projected onto the
  // date scale in the sticky header.
  const [previewSpan, setPreviewSpan] = useState<TimelineDateSpan | null>(null);

  // Lane packing runs over every dated row (not just rows inside the loaded
  // range) so lanes stay stable while the range shifts under infinite scroll.
  const liveSections = useMemo<TimelineSection<T>[]>(
    () =>
      groups.map((group) => ({
        group,
        lanes: packTimelineLanes(
          group.items
            .filter((row): row is DatedTimelineRow<T> => Boolean(row.start && row.end))
            .map((row) => ({ end: row.end, item: row, start: row.start })),
        ),
      })),
    [groups],
  );
  // While a bar is being dragged the sections are frozen: a background refetch
  // that re-packs lanes would otherwise remount the dragged bar mid-gesture.
  // The unfreeze runs pre-paint (layout effect) so swapping the frozen
  // sections for live ones never paints an intermediate frame.
  const [sections, setSections] = useState(liveSections);
  const [activeDragCount, setActiveDragCount] = useState(0);
  useLayoutEffect(() => {
    if (activeDragCount === 0) setSections(liveSections);
  }, [activeDragCount, liveSections]);

  const isEmpty = useMemo(() => liveSections.every((section) => section.lanes.length === 0), [liveSections]);

  const showGroupHeaders = grouped || sections.length > 1;
  const collapsedKeys = useMemo(() => new Set(collapsedGroupKeys ?? []), [collapsedGroupKeys]);
  // Section bands describe where each group lives vertically — the pointer
  // handlers (drag-to-create, vertical bar moves) resolve a group from an
  // offset through them. Empty sections keep one lane of height while
  // grouping is active, so every group stays a draw/drop target.
  const sectionBands = useMemo<TimelineSectionBand[]>(() => {
    let cursor = 0;
    return sections.map((section) => {
      const collapsed = collapsedKeys.has(section.group.key);
      const headerHeight = showGroupHeaders ? GROUP_HEADER_HEIGHT : 0;
      const laneCount = collapsed ? 0 : Math.max(section.lanes.length, grouped ? 1 : 0);
      const laneAreaHeight = laneCount * LANE_HEIGHT;
      const band: TimelineSectionBand = {
        bottom: cursor + headerHeight + laneAreaHeight,
        collapsed,
        key: section.group.key,
        laneAreaHeight,
        laneAreaTop: cursor + headerHeight,
        top: cursor,
      };
      cursor = band.bottom;
      return band;
    });
  }, [collapsedKeys, grouped, sections, showGroupHeaders]);
  const laneRowsHeight = sectionBands[sectionBands.length - 1]?.bottom ?? 0;

  geometryRef.current = { bands: sectionBands, canvasRef, cellWidth, columnCount: columns.length, lanesRef, range, scale, scrollerRef };

  // The group a dragged bar is currently hovering over (highlighted as the
  // vertical drop target). Only ever set by the single active drag.
  const [dropGroupKey, setDropGroupKey] = useState<string | null>(null);

  const pendingCenterRef = useRef<Date | null>(todayTimelineDate());
  const pendingScrollShiftRef = useRef(0);
  const rangeShiftInFlightRef = useRef(false);
  const lastRangeShiftAtRef = useRef(0);
  const centeredRangeRef = useRef<TimelineRange | null>(null);
  const notifyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevScaleRef = useRef(scale);

  const handleDragActiveChange = useCallback((active: boolean) => {
    setActiveDragCount((count) => Math.max(0, count + (active ? 1 : -1)));
  }, []);

  // Drag-to-create: press on empty lane space and draw sideways to sketch a
  // new item's date span (or double-click for a one-day draft). Releasing the
  // pointer turns the sketch into an inline title input; Enter creates the
  // item, Escape or an empty blur discards it.
  const [createDraft, setCreateDraft] = useState<TimelineCreateDraft | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const createDragRef = useRef<{ anchor: Date; groupKey: string | null; moved: boolean; pointerId: number; startClientX: number; top: number } | null>(null);

  const clearCreateDraft = useCallback(() => {
    setCreateDraft(null);
    setPreviewSpan(null);
  }, []);

  // Resolves the lane row (and group) a create gesture lands on. Group
  // headers and collapsed sections are not create targets; the empty area
  // below the last section belongs to the last expanded group.
  const createTargetAtClientY = useCallback((clientY: number): { groupKey: string | null; top: number } | null => {
    const lanes = lanesRef.current;
    if (!lanes) return null;
    const offsetY = clientY - lanes.getBoundingClientRect().top;
    const geometry = geometryRef.current;
    if (!grouped) {
      const maxTop = Math.max(lanes.getBoundingClientRect().height - LANE_HEIGHT, 0);
      const clamped = Math.max(0, Math.min(offsetY, maxTop));
      return { groupKey: null, top: Math.floor(clamped / LANE_HEIGHT) * LANE_HEIGHT };
    }
    const bands = geometry.bands;
    const expanded = bands.filter((band) => !band.collapsed && band.laneAreaHeight > 0);
    if (expanded.length === 0) return null;
    const lastBand = bands[bands.length - 1]!;
    const band = bands.find((candidate) => offsetY >= candidate.top && offsetY < candidate.bottom)
      ?? (offsetY >= lastBand.bottom ? expanded[expanded.length - 1]! : null);
    if (!band || band.collapsed) return null;
    if (offsetY < band.laneAreaTop && offsetY >= band.top) return null;
    const withinY = Math.max(band.laneAreaTop, Math.min(offsetY, band.bottom - 1));
    const laneOffset = Math.min(
      Math.floor((withinY - band.laneAreaTop) / LANE_HEIGHT) * LANE_HEIGHT,
      Math.max(band.laneAreaHeight - LANE_HEIGHT, 0),
    );
    return { groupKey: band.key, top: band.laneAreaTop + laneOffset };
  }, [grouped]);

  function handleLanesPointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    if (!onCreateItem || event.button !== 0 || createDragRef.current) return;
    // Clicks on bars start their own drag; clicks while naming just blur the
    // input, which commits or discards the draft on its own.
    if ((event.target as Element).closest('[data-timeline-task="true"], [data-timeline-draft="true"], button')) return;
    if (createDraft?.stage === "naming") return;
    const anchor = timelineDateAtClientX(geometryRef.current, event.clientX);
    const target = createTargetAtClientY(event.clientY);
    if (!anchor || !target) return;
    event.preventDefault();
    createDragRef.current = { anchor, groupKey: target.groupKey, moved: false, pointerId: event.pointerId, startClientX: event.clientX, top: target.top };
    setIsDrawing(true);
  }

  function handleLanesDoubleClick(event: ReactMouseEvent<HTMLDivElement>) {
    if (!onCreateItem) return;
    if ((event.target as Element).closest('[data-timeline-task="true"], [data-timeline-draft="true"], button')) return;
    const date = timelineDateAtClientX(geometryRef.current, event.clientX);
    const target = createTargetAtClientY(event.clientY);
    if (!date || !target) return;
    setCreateDraft({ groupKey: target.groupKey, span: { end: date, start: date }, stage: "naming", top: target.top });
    setPreviewSpan({ end: date, start: date });
  }

  useEffect(() => {
    if (!isDrawing) return;
    const applyMove = (event: PointerEvent) => {
      const drag = createDragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) return;
      if (!drag.moved && Math.abs(event.clientX - drag.startClientX) <= 3) return;
      drag.moved = true;
      const date = timelineDateAtClientX(geometryRef.current, event.clientX);
      if (!date) return;
      const span = canCreateRange
        ? date < drag.anchor
          ? { end: drag.anchor, start: date }
          : { end: date, start: drag.anchor }
        : { end: date, start: date };
      setCreateDraft((current) => current && timelineSpanEquals(current.span, span) ? current : { groupKey: drag.groupKey, span, stage: "drawing", top: drag.top });
      setPreviewSpan(span);
    };
    const finish = (commit: boolean) => {
      const drag = createDragRef.current;
      createDragRef.current = null;
      setIsDrawing(false);
      if (!drag?.moved || !commit) {
        clearCreateDraft();
        return;
      }
      setCreateDraft((current) => current ? { ...current, stage: "naming" } : current);
    };
    const handleUp = (event: PointerEvent) => {
      if (createDragRef.current && event.pointerId === createDragRef.current.pointerId) finish(true);
    };
    const handleCancel = (event: PointerEvent) => {
      if (createDragRef.current && event.pointerId === createDragRef.current.pointerId) finish(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      finish(false);
    };
    window.addEventListener("pointermove", applyMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleCancel);
    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("pointermove", applyMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleCancel);
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [canCreateRange, clearCreateDraft, isDrawing]);

  const scheduleVisibleRangeNotify = useCallback(() => {
    if (!onVisibleRangeChange) return;
    if (notifyTimerRef.current) clearTimeout(notifyTimerRef.current);
    notifyTimerRef.current = setTimeout(() => {
      const geometry = geometryRef.current;
      const scroller = geometry.scrollerRef.current;
      if (!scroller) return;
      const firstColumn = Math.floor(scroller.scrollLeft / geometry.cellWidth);
      const visibleColumns = Math.ceil(scroller.clientWidth / geometry.cellWidth);
      const buffer = Math.max(4, visibleColumns);
      const startColumn = Math.max(0, firstColumn - buffer);
      const endColumn = Math.min(geometry.columnCount, firstColumn + visibleColumns + buffer);
      onVisibleRangeChange({
        endDate: toTimelineDateInput(timelineDateAtPosition(endColumn, geometry.range.start, geometry.scale)),
        startDate: toTimelineDateInput(timelineDateAtPosition(startColumn, geometry.range.start, geometry.scale)),
      });
    }, 150);
  }, [onVisibleRangeChange]);

  useEffect(() => () => {
    if (notifyTimerRef.current) clearTimeout(notifyTimerRef.current);
  }, []);

  // Scale switched: rebuild the range around the date that was in the middle
  // of the viewport (captured in handleScaleChange) so the user keeps context.
  useLayoutEffect(() => {
    if (prevScaleRef.current === scale) return;
    prevScaleRef.current = scale;
    pendingCenterRef.current ??= todayTimelineDate();
    setRange(buildTimelineViewportRange(scale, pendingCenterRef.current));
  }, [scale]);

  // Apply scroll compensation for range shifts and pending centering before
  // paint, so infinite scroll and scale switches never flash a wrong frame.
  useLayoutEffect(() => {
    if (centeredRangeRef.current === range) return;
    centeredRangeRef.current = range;
    const scroller = scrollerRef.current;
    if (!scroller) return;
    if (pendingScrollShiftRef.current !== 0) {
      scroller.scrollLeft += pendingScrollShiftRef.current;
      pendingScrollShiftRef.current = 0;
    }
    rangeShiftInFlightRef.current = false;
    const center = pendingCenterRef.current;
    if (center) {
      pendingCenterRef.current = null;
      const offset = timelinePosition(center, range.start, scale) * cellWidth;
      scroller.scrollLeft = Math.max(0, offset - scroller.clientWidth / 2);
    }
    scheduleVisibleRangeNotify();
  }, [range, scale, cellWidth, scheduleVisibleRangeNotify]);

  // Both scroll events and the sentinel observer only wake this check up; the
  // decision to shift is always made from the live scroll position, so stale
  // observer records can never trigger a second, bogus shift.
  const checkRangeEdges = useCallback(() => {
    if (rangeShiftInFlightRef.current) return;
    if (performance.now() - lastRangeShiftAtRef.current < 200) return;
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const edgeThreshold = Math.max(EDGE_SENTINEL_WIDTH, scroller.clientWidth * 0.35);
    const direction = scroller.scrollLeft <= edgeThreshold
      ? "backward"
      : scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - edgeThreshold
        ? "forward"
        : null;
    if (!direction) return;
    const geometry = geometryRef.current;
    const nextRange = shiftTimelineRange(geometry.range, geometry.scale, direction);
    const shiftedColumns = Math.round(Math.abs(timelinePosition(nextRange.start, geometry.range.start, geometry.scale)));
    rangeShiftInFlightRef.current = true;
    lastRangeShiftAtRef.current = performance.now();
    pendingScrollShiftRef.current = (direction === "backward" ? 1 : -1) * shiftedColumns * geometry.cellWidth;
    setRange(nextRange);
  }, []);

  // Edge sentinels back up the scroll handler through an IntersectionObserver:
  // unlike scroll events, it also fires for programmatic and composited
  // scrolls, so the range keeps extending no matter how the user got there.
  const startSentinelRef = useRef<HTMLDivElement>(null);
  const endSentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const scroller = scrollerRef.current;
    const startSentinel = startSentinelRef.current;
    const endSentinel = endSentinelRef.current;
    if (!scroller || !startSentinel || !endSentinel) return;
    const observer = new IntersectionObserver(() => {
      checkRangeEdges();
      scheduleVisibleRangeNotify();
    }, { root: scroller });
    observer.observe(startSentinel);
    observer.observe(endSentinel);
    return () => observer.disconnect();
  }, [checkRangeEdges, scheduleVisibleRangeNotify]);

  function handleScroll() {
    checkRangeEdges();
    scheduleVisibleRangeNotify();
  }

  function handleScaleChange(nextScale: TimelineScale) {
    if (nextScale === scale) return;
    const scroller = scrollerRef.current;
    if (scroller) {
      const position = Math.min(columns.length, Math.max(0, (scroller.scrollLeft + scroller.clientWidth / 2) / cellWidth));
      pendingCenterRef.current = timelineDateAtPosition(position, range.start, scale);
    } else {
      pendingCenterRef.current = todayTimelineDate();
    }
    onScaleChange(nextScale);
  }

  function scrollToToday() {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const today = todayTimelineDate();
    const position = timelinePosition(today, range.start, scale);
    if (position < 1 || position > columns.length - 1) {
      pendingCenterRef.current = today;
      setRange(buildTimelineViewportRange(scale));
      return;
    }
    scroller.scrollTo({ behavior: "smooth", left: Math.max(0, position * cellWidth - scroller.clientWidth / 2) });
  }

  const today = todayTimelineDate();
  const todayLineX = ((timelinePosition(today, range.start, scale) + timelinePosition(addTimelineDays(today, 1), range.start, scale)) / 2) * cellWidth;
  const todayVisible = todayLineX >= 0 && todayLineX <= timelineWidth;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <TimelineToolbar onScaleChange={handleScaleChange} onToday={scrollToToday} scale={scale} toolbarEnd={toolbarEnd} />
      {/* `isolate` keeps the sticky header and drag layers inside their own
          stacking context so they never paint over outside overlays like the
          Customize view panel. */}
      <div className="relative isolate min-h-[360px] flex-1">
        <div
          className="flex h-full min-h-[360px] flex-col overflow-auto overscroll-contain rounded-lg border bg-card"
          onScroll={handleScroll}
          ref={scrollerRef}
        >
          <div className="relative flex min-w-full grow flex-col" ref={canvasRef} style={{ width: timelineWidth }}>
            <div aria-hidden className="pointer-events-none absolute inset-y-0 left-0" ref={startSentinelRef} style={{ width: EDGE_SENTINEL_WIDTH }} />
            <div aria-hidden className="pointer-events-none absolute inset-y-0 right-0" ref={endSentinelRef} style={{ width: EDGE_SENTINEL_WIDTH }} />
            <TimelineHeader
              cellWidth={cellWidth}
              columns={columns}
              columnTemplate={columnTemplate}
              previewSpan={previewSpan}
              range={range}
              scale={scale}
              timelineWidth={timelineWidth}
              today={today}
            />
            <div
              className="relative flex-1"
              onDoubleClick={handleLanesDoubleClick}
              onPointerDown={handleLanesPointerDown}
              ref={lanesRef}
              style={{ minHeight: Math.max(laneRowsHeight + 96, 240) }}
            >
              <div aria-hidden className="pointer-events-none absolute inset-0 grid" style={{ gridTemplateColumns: columnTemplate }}>
                {columns.map((column, index) => (
                  <div
                    className={cn(
                      "border-r",
                      isPeriodEnd(columns, index, scale) ? "border-border/50" : "border-dashed border-muted",
                      column.isWeekend && "bg-muted/30",
                    )}
                    key={column.key}
                  />
                ))}
              </div>
              {todayVisible ? (
                <div aria-hidden className="pointer-events-none absolute inset-y-0 z-10 w-px bg-destructive/80" style={{ left: todayLineX }}>
                  <span className="absolute -top-1 -left-[3.5px] size-2 rounded-full bg-destructive" />
                </div>
              ) : null}
              <div className="relative">
                {sections.map((section) => {
                  const collapsed = collapsedKeys.has(section.group.key);
                  const itemCount = section.lanes.reduce((count, lane) => count + lane.length, 0);
                  return (
                    <div
                      className={cn("transition-colors", dropGroupKey === section.group.key && "bg-primary/5")}
                      key={section.group.key}
                    >
                      {showGroupHeaders ? (
                        <div className="relative bg-muted/20" style={{ height: GROUP_HEADER_HEIGHT }}>
                          <button
                            className="sticky left-3 inline-flex w-max cursor-pointer items-center gap-1.5 text-[11px] font-semibold text-foreground/85 outline-none focus-visible:ring-2 focus-visible:ring-ring/70"
                            onClick={() => onToggleGroupCollapse?.(section.group.key)}
                            style={{ lineHeight: `${GROUP_HEADER_HEIGHT}px` }}
                            type="button"
                          >
                            {onToggleGroupCollapse ? (
                              <ChevronRight aria-hidden className={cn("size-3.5 text-muted-foreground transition-transform", !collapsed && "rotate-90")} />
                            ) : null}
                            {section.group.color ? <span aria-hidden className="size-2 rounded-full" style={{ backgroundColor: section.group.color }} /> : null}
                            {section.group.label}
                            <span className="font-normal text-muted-foreground tabular-nums">{itemCount}</span>
                          </button>
                        </div>
                      ) : null}
                      {collapsed ? null : section.lanes.length === 0 && grouped ? (
                        <div style={{ height: LANE_HEIGHT }} />
                      ) : (
                        section.lanes.map((lane, laneIndex) => (
                          <div className="relative" key={`${section.group.key}:${laneIndex}`} style={{ height: LANE_HEIGHT }}>
                            {lane
                              .filter((packed) => packed.end >= range.start && packed.start <= range.end)
                              .map((packed) => (
                                <TimelineTask
                                  cellWidth={cellWidth}
                                  geometry={geometryRef}
                                  key={packed.item.id}
                                  onChangeDates={onChangeItemDates}
                                  onChangeGroup={grouped ? onChangeItemGroup : undefined}
                                  onClick={() => onItemClick(packed.item.item)}
                                  onDragActiveChange={handleDragActiveChange}
                                  onHoverGroupChange={setDropGroupKey}
                                  onPreviewSpanChange={setPreviewSpan}
                                  range={range}
                                  row={packed.item}
                                  scale={scale}
                                  sectionKey={section.group.key}
                                  timelineWidth={timelineWidth}
                                />
                              ))}
                          </div>
                        ))
                      )}
                    </div>
                  );
                })}
              </div>
              {createDraft && onCreateItem ? (
                <TimelineDraftBar
                  cellWidth={cellWidth}
                  draft={createDraft}
                  onCancel={clearCreateDraft}
                  onCommit={async (title) => {
                    await onCreateItem({ groupKey: createDraft.groupKey, span: createDraft.span, title });
                    clearCreateDraft();
                  }}
                  range={range}
                  scale={scale}
                  timelineWidth={timelineWidth}
                />
              ) : null}
            </div>
          </div>
        </div>

        {isEmpty && !loading && sections.length === 0 ? (
          <div className="pointer-events-none absolute inset-0 z-20 grid place-items-center p-6">
            <div className="flex max-w-xs flex-col items-center gap-2 text-center">
              <div className="grid size-10 place-items-center rounded-full border border-border/60 bg-muted/40">
                <CalendarRange aria-hidden className="size-5 text-muted-foreground" />
              </div>
              <p className="text-sm text-muted-foreground">{emptyMessage}</p>
            </div>
          </div>
        ) : null}
        {isEmpty && loading ? (
          <div aria-hidden className="pointer-events-none absolute inset-x-0 top-[88px] z-20 flex flex-col gap-5 px-8">
            {[28, 42, 22].map((width, index) => (
              <div
                className="h-6 animate-pulse rounded-md bg-muted/70"
                key={index}
                style={{ marginLeft: `${index * 14 + 6}%`, width: `${width}%` }}
              />
            ))}
          </div>
        ) : null}
        {!isEmpty && loading ? (
          <div className="pointer-events-none absolute top-2 right-3 z-20 flex items-center gap-1.5 rounded-md border border-border/60 bg-popover/95 px-2 py-1 text-[10px] font-medium text-muted-foreground shadow-sm">
            <Loader2 aria-hidden className="size-3 animate-spin" />
            {t("board.loading")}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function TimelineToolbar({
  onScaleChange,
  onToday,
  scale,
  toolbarEnd,
}: {
  onScaleChange: (scale: TimelineScale) => void;
  onToday: () => void;
  scale: TimelineScale;
  toolbarEnd?: ReactNode;
}) {
  const t = useT();
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-2">
        <Button onClick={onToday} size="sm" variant="outline">
          <CalendarDays />
          {t("board.today")}
        </Button>
        <div aria-label={t("board.timelineScale")} className="flex items-center rounded-md bg-muted/40 p-0.5" role="group">
          {(["days", "weeks", "months"] as TimelineScale[]).map((option) => (
            <button
              aria-pressed={scale === option}
              className={cn(
                "h-7 rounded px-2.5 text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground",
                scale === option && "bg-card text-foreground shadow-xs",
              )}
              key={option}
              onClick={() => onScaleChange(option)}
              type="button"
            >
              {timelineScaleLabels[option]}
            </button>
          ))}
        </div>
      </div>
      {toolbarEnd ? <div className="flex min-w-0 items-center gap-3">{toolbarEnd}</div> : null}
    </div>
  );
}

function TimelineHeader({
  cellWidth,
  columns,
  columnTemplate,
  previewSpan,
  range,
  scale,
  timelineWidth,
  today,
}: {
  cellWidth: number;
  columns: TimelineColumn[];
  columnTemplate: string;
  previewSpan: TimelineDateSpan | null;
  range: TimelineRange;
  scale: TimelineScale;
  timelineWidth: number;
  today: Date;
}) {
  const preview = useMemo(() => {
    if (!previewSpan) return null;
    const bar = timelineBarPosition(previewSpan, range, scale);
    const rawLeft = bar.left * cellWidth;
    const rawRight = (bar.left + bar.width) * cellWidth;
    const left = Math.max(0, rawLeft);
    const width = Math.max(Math.min(rawRight, timelineWidth) - left, 2);
    return {
      left,
      singleDay: previewSpan.start.getTime() === previewSpan.end.getTime(),
      width,
    };
  }, [cellWidth, previewSpan, range, scale, timelineWidth]);
  const periodGroups = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(undefined, { month: "long", timeZone: "UTC", year: "numeric" });
    return columns.reduce<Array<{ key: string; label: string; span: number }>>((groups, column) => {
      const key = scale === "months" ? String(column.start.getUTCFullYear()) : column.monthKey;
      const current = groups[groups.length - 1];
      if (current?.key === key) {
        current.span += 1;
        return groups;
      }
      groups.push({ key, label: scale === "months" ? String(column.start.getUTCFullYear()) : formatter.format(column.start), span: 1 });
      return groups;
    }, []);
  }, [columns, scale]);

  return (
    <div className="sticky top-0 z-30 border-b bg-card/95 backdrop-blur-sm">
      <div className="grid h-7" style={{ gridTemplateColumns: columnTemplate }}>
        {periodGroups.map((group) => (
          <div className="relative" key={group.key} style={{ gridColumn: `span ${group.span}` }}>
            <span className="sticky left-2 inline-block w-max px-1 text-[10px] font-medium tracking-wide text-muted-foreground uppercase" style={{ lineHeight: "27px" }}>
              {group.label}
            </span>
          </div>
        ))}
      </div>
      <div className="relative grid h-9 bg-muted/30" style={{ gridTemplateColumns: columnTemplate }}>
        {preview && previewSpan ? (
          <div
            className="pointer-events-none absolute inset-y-1 z-10 flex items-center justify-between gap-2 overflow-hidden rounded-md bg-secondary px-2 whitespace-nowrap shadow-sm"
            style={{ left: preview.left, width: preview.width }}
          >
            <span className="text-[11px] font-medium text-foreground">{formatTimelineDateShort(previewSpan.start)}</span>
            {!preview.singleDay && preview.width >= 128 ? (
              <span className="text-[11px] font-medium text-foreground">{formatTimelineDateShort(previewSpan.end)}</span>
            ) : null}
          </div>
        ) : null}
        {columns.map((column) => {
          const isToday = scale === "days" && column.start.getTime() === today.getTime();
          return (
            <div
              className={cn("flex flex-col items-center justify-center gap-px", column.isWeekend && "bg-muted/25")}
              key={column.key}
            >
              {scale === "days" ? (
                <>
                  <span className={cn("text-[9px]", isToday ? "font-medium text-destructive" : "text-muted-foreground/70")}>{column.subLabel}</span>
                  <span className={cn("text-xs tabular-nums", isToday ? "font-semibold text-destructive" : "text-muted-foreground")}>
                    {column.label}
                  </span>
                </>
              ) : (
                <>
                  <span className="max-w-full truncate px-1.5 text-xs text-muted-foreground tabular-nums">{column.label}</span>
                  <span className="text-[9px] text-muted-foreground/60 tabular-nums">{column.subLabel}</span>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function isPeriodEnd(columns: TimelineColumn[], index: number, scale: TimelineScale) {
  const next = columns[index + 1];
  if (!next) return true;
  const column = columns[index]!;
  if (scale === "months") return next.start.getUTCFullYear() !== column.start.getUTCFullYear();
  return next.monthKey !== column.monthKey;
}

type TimelineCreateDraft = {
  /** Section the item is being sketched into; null when grouping is off. */
  groupKey: string | null;
  span: TimelineDateSpan;
  stage: "drawing" | "naming";
  /** Vertical offset of the sketched lane within the lanes container. */
  top: number;
};

function timelineDateAtClientX(geometry: TimelineGeometry, clientX: number) {
  const canvas = geometry.canvasRef.current;
  if (!canvas) return null;
  const rect = canvas.getBoundingClientRect();
  const position = Math.min(geometry.columnCount, Math.max(0, (clientX - rect.left) / geometry.cellWidth));
  return timelineDateAtPosition(position, geometry.range.start, geometry.scale);
}

function TimelineDraftBar({
  cellWidth,
  draft,
  onCancel,
  onCommit,
  range,
  scale,
  timelineWidth,
}: {
  cellWidth: number;
  draft: TimelineCreateDraft;
  onCancel: () => void;
  onCommit: (title: string) => Promise<void>;
  range: TimelineRange;
  scale: TimelineScale;
  timelineWidth: number;
}) {
  const t = useT();
  const [title, setTitle] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const committedRef = useRef(false);
  const bar = timelineBarPosition(draft.span, range, scale);
  const rawLeft = bar.left * cellWidth;
  const rawRight = (bar.left + bar.width) * cellWidth;
  const left = Math.max(0, rawLeft);
  const width = Math.max(Math.min(rawRight, timelineWidth) - left, 18);
  const durationDays = Math.round((draft.span.end.getTime() - draft.span.start.getTime()) / dayMilliseconds) + 1;

  async function commit() {
    if (committedRef.current) return;
    const trimmed = title.trim();
    if (!trimmed) {
      onCancel();
      return;
    }
    committedRef.current = true;
    setIsSaving(true);
    try {
      await onCommit(trimmed);
    } catch {
      // Creation failed; keep the input so the title is not lost.
      committedRef.current = false;
      setIsSaving(false);
    }
  }

  return (
    <div
      className={cn(
        "absolute z-30 flex items-center gap-2 rounded-md border border-dashed border-primary/50 bg-primary/10 px-2 text-xs",
        draft.stage === "drawing" && "pointer-events-none",
      )}
      data-timeline-draft="true"
      style={{ height: LANE_HEIGHT - 8, left, top: draft.top + 4, width }}
    >
      {draft.stage === "naming" ? (
        <input
          autoFocus
          className="w-full min-w-0 bg-transparent font-medium text-foreground outline-none placeholder:text-muted-foreground/60"
          disabled={isSaving}
          onBlur={() => {
            if (!committedRef.current) void commit();
          }}
          onChange={(event) => setTitle(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void commit();
            } else if (event.key === "Escape") {
              event.preventDefault();
              onCancel();
            }
          }}
          placeholder={t("board.newItem")}
          value={title}
        />
      ) : width >= 72 ? (
        <span className="ml-auto text-[10px] text-muted-foreground/80 tabular-nums">{durationDays}d</span>
      ) : null}
      {isSaving ? <Loader2 aria-hidden className="size-3 shrink-0 animate-spin text-muted-foreground" /> : null}
    </div>
  );
}

type DragMode = "move" | "resize-end" | "resize-start";

type DragState = {
  grabOffsetDays: number;
  hoverGroupKey: string | null;
  lastClientX: number;
  mode: DragMode;
  moved: boolean;
  originSpan: TimelineDateSpan;
  pointerId: number;
  startClientX: number;
  startClientY: number;
};

const dayMilliseconds = 24 * 60 * 60 * 1000;

function TimelineTask<T>({
  cellWidth,
  geometry,
  onChangeDates,
  onChangeGroup,
  onClick,
  onDragActiveChange,
  onHoverGroupChange,
  onPreviewSpanChange,
  range,
  row,
  scale,
  sectionKey,
  timelineWidth,
}: {
  cellWidth: number;
  geometry: RefObject<TimelineGeometry>;
  onChangeDates?: (item: T, span: TimelineDateSpan) => Promise<void>;
  onChangeGroup?: (item: T, groupKey: string) => Promise<void>;
  onClick: () => void;
  onDragActiveChange: (active: boolean) => void;
  onHoverGroupChange: (groupKey: string | null) => void;
  onPreviewSpanChange: (span: TimelineDateSpan | null) => void;
  range: TimelineRange;
  row: DatedTimelineRow<T>;
  scale: TimelineScale;
  sectionKey: string;
  timelineWidth: number;
}) {
  const t = useT();
  const [draftSpan, setDraftSpanState] = useState<TimelineDateSpan>({ end: row.end, start: row.start });
  const [dragMode, setDragMode] = useState<DragMode | null>(null);
  const [dragOffsetY, setDragOffsetY] = useState(0);
  const [preview, setPreview] = useState<DragMode | "keyboard" | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const draftRef = useRef(draftSpan);
  const dragRef = useRef<DragState | null>(null);
  const savingRef = useRef(false);
  const suppressClickRef = useRef(false);
  const rowSpanRef = useRef<TimelineDateSpan>({ end: row.end, start: row.start });
  rowSpanRef.current = { end: row.end, start: row.start };
  const keyboardCommitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoScrollRef = useRef<{ frame: number; velocity: number } | null>(null);

  const interactive = Boolean(onChangeDates);
  const canResize = interactive && (row.canResize ?? true);
  const canMoveBetweenGroups = interactive && Boolean(onChangeGroup);

  const setDraft = useCallback((span: TimelineDateSpan) => {
    draftRef.current = span;
    setDraftSpanState(span);
  }, []);

  // The freeze counter in the parent must stay balanced even if a drag ends
  // through an unexpected path, so notify only on real transitions and always
  // release on unmount.
  const dragActiveNotifiedRef = useRef(false);
  const notifyDragActive = useCallback((active: boolean) => {
    if (dragActiveNotifiedRef.current === active) return;
    dragActiveNotifiedRef.current = active;
    onDragActiveChange(active);
  }, [onDragActiveChange]);
  useEffect(() => () => notifyDragActive(false), [notifyDragActive]);

  // Keep the draft in sync with server data, but never while the user is
  // mid-gesture or a save is in flight — a refetch must not yank the bar.
  const rowStartMs = row.start.getTime();
  const rowEndMs = row.end.getTime();
  useEffect(() => {
    if (dragRef.current || savingRef.current || keyboardCommitTimerRef.current) return;
    const next = { end: new Date(rowEndMs), start: new Date(rowStartMs) };
    if (!timelineSpanEquals(draftRef.current, next)) {
      draftRef.current = next;
      setDraftSpanState(next);
    }
  }, [rowStartMs, rowEndMs, dragMode, isSaving]);

  const stopAutoScroll = useCallback(() => {
    if (autoScrollRef.current) {
      cancelAnimationFrame(autoScrollRef.current.frame);
      autoScrollRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    stopAutoScroll();
    if (keyboardCommitTimerRef.current) clearTimeout(keyboardCommitTimerRef.current);
  }, [stopAutoScroll]);

  // Project the draft span onto the header date scale while a gesture is live.
  const previewReportedRef = useRef(false);
  useEffect(() => {
    if (preview !== null) {
      previewReportedRef.current = true;
      onPreviewSpanChange(draftSpan);
    } else if (previewReportedRef.current) {
      previewReportedRef.current = false;
      onPreviewSpanChange(null);
    }
  }, [draftSpan, onPreviewSpanChange, preview]);
  useEffect(() => () => {
    if (previewReportedRef.current) onPreviewSpanChange(null);
  }, [onPreviewSpanChange]);
  // Release the drop-target highlight if the bar unmounts mid-drag (e.g. the
  // group change lands and the row re-packs into another section).
  useEffect(() => () => {
    if (dragRef.current?.hoverGroupKey) onHoverGroupChange(null);
  }, [onHoverGroupChange]);

  function dateAtClientX(clientX: number) {
    return timelineDateAtClientX(geometry.current, clientX);
  }

  function applyPointer(clientX: number) {
    const drag = dragRef.current;
    if (!drag) return;
    const pointerDate = dateAtClientX(clientX);
    if (!pointerDate) return;
    let nextSpan: TimelineDateSpan;
    if (drag.mode === "move") {
      const deltaDays = Math.round((pointerDate.getTime() - drag.originSpan.start.getTime()) / dayMilliseconds) - drag.grabOffsetDays;
      nextSpan = moveTimelineSpan(drag.originSpan, deltaDays);
    } else {
      nextSpan = resizeTimelineSpan(drag.originSpan, drag.mode === "resize-start" ? "start" : "end", pointerDate);
    }
    if (!timelineSpanEquals(nextSpan, draftRef.current)) setDraft(nextSpan);
  }

  function updateAutoScroll(clientX: number) {
    const scroller = geometry.current.scrollerRef.current;
    if (!scroller) return;
    const rect = scroller.getBoundingClientRect();
    const zone = 48;
    let velocity = 0;
    if (clientX < rect.left + zone) velocity = -Math.min(24, Math.ceil((rect.left + zone - clientX) / 3));
    else if (clientX > rect.right - zone) velocity = Math.min(24, Math.ceil((clientX - (rect.right - zone)) / 3));
    if (velocity === 0) {
      stopAutoScroll();
      return;
    }
    if (autoScrollRef.current) {
      autoScrollRef.current.velocity = velocity;
      return;
    }
    const tick = () => {
      const drag = dragRef.current;
      const state = autoScrollRef.current;
      const currentScroller = geometry.current.scrollerRef.current;
      if (!drag || !state || !currentScroller) return;
      currentScroller.scrollLeft += state.velocity;
      applyPointer(drag.lastClientX);
      state.frame = requestAnimationFrame(tick);
    };
    autoScrollRef.current = { frame: requestAnimationFrame(tick), velocity };
  }

  function startDrag(mode: DragMode, event: ReactPointerEvent<HTMLElement>) {
    if (!interactive || savingRef.current || dragRef.current) return;
    if (mode !== "move" && !canResize) return;
    if (event.button !== 0) return;
    const pointerDate = dateAtClientX(event.clientX);
    if (!pointerDate) return;
    event.preventDefault();
    event.stopPropagation();
    const originSpan = draftRef.current;
    dragRef.current = {
      grabOffsetDays: Math.round((pointerDate.getTime() - originSpan.start.getTime()) / dayMilliseconds),
      hoverGroupKey: null,
      lastClientX: event.clientX,
      mode,
      moved: false,
      originSpan,
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
    };
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Capture is best-effort; the window-level listeners below keep the
      // drag working even without it.
    }
    setDragMode(mode);
    notifyDragActive(true);
  }

  function handlePointerMove(clientX: number, clientY: number) {
    const drag = dragRef.current;
    if (!drag) return;
    drag.lastClientX = clientX;
    if (!drag.moved && (Math.abs(clientX - drag.startClientX) > 3 || (canMoveBetweenGroups && drag.mode === "move" && Math.abs(clientY - drag.startClientY) > 3))) {
      drag.moved = true;
      setPreview(drag.mode);
    }
    if (!drag.moved) return;
    applyPointer(clientX);
    if (canMoveBetweenGroups && drag.mode === "move") {
      setDragOffsetY(clientY - drag.startClientY);
      const hover = hoverGroupKeyAtClientY(clientY);
      if (hover !== drag.hoverGroupKey) {
        drag.hoverGroupKey = hover;
        onHoverGroupChange(hover);
      }
    }
    updateAutoScroll(clientX);
  }

  // The section band under the pointer, as a vertical drop target. The bar's
  // own section and collapsed sections resolve to null (nothing to change).
  function hoverGroupKeyAtClientY(clientY: number) {
    const currentGeometry = geometry.current;
    const lanes = currentGeometry.lanesRef.current;
    if (!lanes) return null;
    const offsetY = clientY - lanes.getBoundingClientRect().top;
    const bands = currentGeometry.bands;
    const expanded = bands.filter((band) => !band.collapsed);
    if (expanded.length === 0) return null;
    const band = bands.find((candidate) => offsetY >= candidate.top && offsetY < candidate.bottom && !candidate.collapsed)
      ?? (offsetY < 0 ? expanded[0]! : offsetY >= bands[bands.length - 1]!.bottom ? expanded[expanded.length - 1]! : null);
    if (!band || band.key === sectionKey) return null;
    return band.key;
  }

  function endDrag(commit: boolean) {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    stopAutoScroll();
    setDragMode(null);
    setPreview(null);
    notifyDragActive(false);
    if (drag.hoverGroupKey) onHoverGroupChange(null);
    suppressClickRef.current = drag.moved;
    const groupCommit = commit && drag.moved ? drag.hoverGroupKey : null;
    // On a group change the bar keeps its dropped translateY: the row is
    // about to re-pack into the target section (unmounting this instance),
    // and resetting the offset first would paint a frame with the bar back
    // in its old section at the new dates. Any path that does not remount
    // (no-op change, failure) clears the offset when the save settles.
    if (!groupCommit) setDragOffsetY(0);
    if (!drag.moved) return;
    if (commit) commitChange(drag.originSpan, draftRef.current, drag.hoverGroupKey);
    else setDraft(drag.originSpan);
  }

  // Pointer tracking lives on the window while a drag is active: it works
  // both with and without pointer capture and survives the pointer leaving
  // the bar. Escape cancels the drag and restores the original dates. The
  // handlers only touch refs, so stale closures are safe here.
  useEffect(() => {
    if (!dragMode) return;
    const handleMove = (event: PointerEvent) => {
      if (dragRef.current && event.pointerId === dragRef.current.pointerId) handlePointerMove(event.clientX, event.clientY);
    };
    const handleUp = (event: PointerEvent) => {
      if (dragRef.current && event.pointerId === dragRef.current.pointerId) endDrag(true);
    };
    const handleCancel = (event: PointerEvent) => {
      if (dragRef.current && event.pointerId === dragRef.current.pointerId) endDrag(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      endDrag(false);
    };
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleCancel);
    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleCancel);
      window.removeEventListener("keydown", handleKeyDown, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragMode]);

  function commitChange(originSpan: TimelineDateSpan, nextSpan: TimelineDateSpan, nextGroupKey: string | null) {
    const spanChanged = Boolean(onChangeDates) && !timelineSpanEquals(originSpan, nextSpan);
    const groupKey = onChangeGroup && nextGroupKey ? nextGroupKey : null;
    if (!spanChanged && !groupKey) return;
    savingRef.current = true;
    setIsSaving(true);
    // Both callbacks are invoked up front (not awaited in sequence) so their
    // optimistic cache patches land in the same frame — otherwise a combined
    // dates+group drop briefly shows the bar in its old group at the new
    // dates while the dates request is in flight. The parent serializes the
    // actual mutations per work object.
    const tasks: Promise<void>[] = [];
    if (spanChanged && onChangeDates) tasks.push(onChangeDates(row.item, nextSpan));
    if (groupKey && onChangeGroup) tasks.push(onChangeGroup(row.item, groupKey));
    void Promise.all(tasks)
      .catch(() => {
        // The parent rolls its cache back on failure; the sync effect
        // snaps the bar back to the server dates once saving clears.
      })
      .finally(() => {
        savingRef.current = false;
        setIsSaving(false);
        // No-ops if the group change re-packed the row (this instance is
        // gone by then); otherwise drops the kept translateY.
        setDragOffsetY(0);
      });
  }

  function handleKeyDown(event: ReactKeyboardEvent<HTMLElement>) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick();
      return;
    }
    if (!interactive) return;
    if (event.key === "Escape" && keyboardCommitTimerRef.current) {
      event.preventDefault();
      clearTimeout(keyboardCommitTimerRef.current);
      keyboardCommitTimerRef.current = null;
      setPreview(null);
      setDraft(rowSpanRef.current);
      return;
    }
    const delta = event.key === "ArrowLeft" ? -1 : event.key === "ArrowRight" ? 1 : 0;
    if (delta === 0 || savingRef.current) return;
    event.preventDefault();
    const current = draftRef.current;
    const nextSpan = event.shiftKey
      ? canResize
        ? resizeTimelineSpan(current, "end", addTimelineDays(current.end, delta))
        : current
      : moveTimelineSpan(current, delta);
    if (timelineSpanEquals(nextSpan, current)) return;
    setDraft(nextSpan);
    setPreview("keyboard");
    if (keyboardCommitTimerRef.current) clearTimeout(keyboardCommitTimerRef.current);
    keyboardCommitTimerRef.current = setTimeout(() => {
      keyboardCommitTimerRef.current = null;
      setPreview(null);
      commitChange(rowSpanRef.current, draftRef.current, null);
    }, 600);
  }

  function handleClick() {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    onClick();
  }

  const bar = timelineBarPosition(draftSpan, range, scale);
  const rawLeft = bar.left * cellWidth;
  const rawRight = (bar.left + bar.width) * cellWidth;
  if (rawRight < 0 || rawLeft > timelineWidth) return null;
  const left = Math.max(0, rawLeft);
  const width = Math.max(Math.min(rawRight, timelineWidth) - left, 18);
  const clippedStart = rawLeft < -1;
  const clippedEnd = rawRight > timelineWidth + 1;
  const color = row.color ?? "var(--primary)";
  const isDraggingBar = dragMode !== null;
  const compact = width < 40;
  const showStartHandle = canResize && width >= 48;
  const durationDays = Math.round((draftSpan.end.getTime() - draftSpan.start.getTime()) / dayMilliseconds) + 1;

  return (
    <div
      aria-busy={isSaving}
      aria-label={`${row.label}, ${formatTimelineDateSpan(draftSpan)}`}
      className={cn(
        "group absolute inset-y-1 z-20 flex items-center overflow-visible rounded-md text-left text-xs outline-none select-none",
        "transition-[background-color,box-shadow,opacity] duration-100",
        "focus-visible:ring-2 focus-visible:ring-ring/70",
        compact
          ? "justify-center gap-0 bg-[color-mix(in_oklab,var(--bar-color)_45%,transparent)] px-0"
          : "gap-2 bg-[color-mix(in_oklab,var(--bar-color)_10%,transparent)] px-2 hover:bg-[color-mix(in_oklab,var(--bar-color)_16%,transparent)]",
        interactive ? "touch-none" : "cursor-pointer",
        interactive && !isDraggingBar && "cursor-grab",
        isDraggingBar && "cursor-grabbing bg-[color-mix(in_oklab,var(--bar-color)_20%,transparent)] shadow-md ring-1 ring-[color-mix(in_oklab,var(--bar-color)_45%,transparent)]",
        (isDraggingBar || dragOffsetY !== 0) && "z-30",
        isSaving && "opacity-75",
        clippedStart && "rounded-l-none",
        clippedEnd && "rounded-r-none",
      )}
      data-timeline-task="true"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onPointerDown={(event) => startDrag("move", event)}
      role="button"
      style={{
        "--bar-color": color,
        left,
        transform: dragOffsetY !== 0 ? `translateY(${dragOffsetY}px)` : undefined,
        width,
      } as CSSProperties}
      tabIndex={0}
    >
      {showStartHandle ? (
        <button
          aria-label={t("a11y.changeStartDate", { name: row.label })}
          className="absolute inset-y-0 left-0 z-10 flex w-3 cursor-ew-resize items-center justify-center rounded-l-md opacity-0 outline-none transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
          onClick={(event) => event.stopPropagation()}
          onPointerDown={(event) => startDrag("resize-start", event)}
          tabIndex={-1}
          type="button"
        >
          <span className="h-3.5 w-0.5 rounded-full bg-foreground/45" />
        </button>
      ) : null}
      {!compact ? (
        <>
          {/* The inner span is sticky so the label stays readable while a long
              bar is scrolled past the left edge of the viewport. The label
              never shrinks (it only truncates once it alone exceeds the bar);
              the meta is the sole shrinkable item, so it always collapses
              first — Chrome's flex-shrink weighting between the two is not
              reliable enough to express this. Overflowing chips fade out
              through the mask instead of clipping hard, and the duration label
              on the right never moves. */}
          <span className="pointer-events-none min-w-0 flex-1">
            <span className="sticky left-2 inline-flex max-w-full items-center gap-2 align-middle">
              <span className="max-w-full shrink-0 truncate font-medium text-foreground/90">{row.label}</span>
              {row.meta && row.meta.length > 0 && width >= 120 ? (
                <span className="flex min-w-0 shrink items-center gap-1 overflow-hidden pr-3 [mask-image:linear-gradient(to_right,black_calc(100%-12px),transparent)]">
                  {row.meta.map((part, partIndex) => (
                    <span
                      className="inline-flex max-w-36 shrink-0 items-center gap-1 rounded-sm bg-background/55 px-1.5 py-px text-[10px] font-normal text-muted-foreground"
                      key={partIndex}
                    >
                      {part.color ? <span aria-hidden className="size-1.5 shrink-0 rounded-full" style={{ backgroundColor: part.color }} /> : null}
                      <span className="truncate">{part.label}</span>
                    </span>
                  ))}
                </span>
              ) : null}
            </span>
          </span>
          {isSaving ? (
            <Loader2 aria-hidden className="size-3 shrink-0 animate-spin text-muted-foreground" />
          ) : width >= 72 ? (
            <span className="pointer-events-none shrink-0 text-[10px] text-muted-foreground/80 tabular-nums">{durationDays}d</span>
          ) : null}
        </>
      ) : null}
      {canResize ? (
        <button
          aria-label={t("a11y.changeEndDate", { name: row.label })}
          className="absolute inset-y-0 right-0 z-10 flex w-3 cursor-ew-resize items-center justify-center rounded-r-md opacity-0 outline-none transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
          onClick={(event) => event.stopPropagation()}
          onPointerDown={(event) => startDrag("resize-end", event)}
          tabIndex={-1}
          type="button"
        >
          <span className="h-3.5 w-0.5 rounded-full bg-foreground/45" />
        </button>
      ) : null}
    </div>
  );
}
